// Measure-class overrides.
//
// The app INFERS a field's class from its name and a sample of its values, and
// an unrecognised field falls back to `rate` — the class that declines to sum.
// That inference is a guess, and a guess that silently drives arithmetic is
// exactly the thing that should be visible and correctable. So the workbook can
// override any field's class, the override is persisted with it, and the UI
// says which of the two is in force.

import type { GiraffeData, MeasureClass, SourceKind } from './types';
import { unitsOf } from './units';

/** Overrides are keyed `<source>:<field>` — the same field name can differ per source. */
export function overrideKey(source: SourceKind, field: string): string {
  return `${source}:${field}`;
}

/**
 * Wrap a provider so `classOf` consults the workbook's overrides first, while
 * `inferredClassOf` still reports what the app worked out on its own.
 */
export function withClassOverrides(
  base: GiraffeData,
  overrides: Readonly<Record<string, MeasureClass>>
): GiraffeData {
  return {
    get version() {
      return base.version;
    },
    get live() {
      return base.live;
    },
    units: () => unitsOf(base),
    rows: (source) => base.rows(source),
    blocks: () => base.blocks(),
    fields: (source) => base.fields(source),
    classOf: (source, field) => overrides[overrideKey(source, field)] ?? base.classOf(source, field),
    inferredClassOf: (source, field) =>
      base.inferredClassOf?.(source, field) ?? base.classOf(source, field),
    selectionKeys: () => base.selectionKeys(),
    siteArea: () => base.siteArea(),
    geometryFor: (id) => base.geometryFor?.(id),
    drawingLayers: () => base.drawingLayers?.() ?? [],
    visibleDrawingLayers: () => base.visibleDrawingLayers?.() ?? [],
  };
}

export const CLASS_LABELS: Record<MeasureClass, string> = {
  plan: 'Plan — one level per section, sums',
  total: 'Total — levels already applied, sums',
  rate: 'Rate — per unit, area-weighted mean',
  text: 'Text — not a measure',
  nested: 'Nested — an object, shown as a summary',
};

/** Why the app thinks a field is what it is, in one sentence. */
export function whyClass(field: string, cls: MeasureClass, overridden: boolean): string {
  if (overridden) return `Set by hand to ${cls}.`;
  switch (cls) {
    case 'plan':
      return `Inferred: “${field}” is a known footprint measure, so it sums across sections.`;
    case 'total':
      return `Inferred: “${field}” is a whole-stack measure — levels are already applied — so it sums.`;
    case 'text':
      return `Inferred from its values: “${field}” holds text, not numbers.`;
    case 'nested':
      return `Inferred from its values: “${field}” holds an object, so it can only be shown as a summary.`;
    case 'rate':
    default:
      return `Inferred: “${field}” reads as a per-unit rate, or the app did not recognise it — unknown fields default to rate, the class that declines to sum. Change it here if that is wrong.`;
  }
}
